import { AxiosResponse } from 'axios';
import qs from 'qs';
import { axiosApiInstance } from 'Store';
import { convertPrice } from 'utils/convertPrice';
import {
    TenderOfferDetailsResponse,
    TenderOfferResponseContent,
    TenderOffersResponse
} from 'utils/state-managment/tender/tenderOffer';
import { TenderSavedFilter } from 'utils/types/Filter';
import { TenderFilterReturnData } from 'utils/types/Tender';
import { TenderDetailsResponse } from 'utils/types/TenderModels';
import { LocationItem } from 'utils/types/V2Locations';

import { activeNotification } from 'components/functions/activeNotification';

const getSavedFilters = async (): Promise<TenderSavedFilter[]> => {
    return axiosApiInstance
        .get<TenderSavedFilter[]>('/v2/tender-offer/filter/list')
        .then((resp) => {
            if (resp.data) {
                return resp.data.map((el) => ({
                    ...el,
                    name: el.name
                }));
            }

            return [];
        })
        .catch((err) => {
            if (err?.response?.status === 401) {
                window.parent.location.reload();
            }

            return [];
        });
};

const getFilterDetailsTender = async (filterId: number): Promise<TenderFilterReturnData> => {
    return axiosApiInstance
        .get(`/v2/tender-offer/filter/details?id=${filterId}`)
        .then((resp) => {
            const filters = resp.data;
            // Backend zwraca oba pola: locations (stare ID, dla kompatybilności starej apki mobilnej) i filterLocations (nowe OSM ID).
            // Dla nowej apki czytamy filterLocations.
            const savedLocations = Array.isArray(filters.filterLocations)
                ? Object.fromEntries(
                    filters.filterLocations.map((location: LocationItem) => [
                        location.id,
                        // @ts-expect-error z Api dostajemy fullName
                        { ...location, label: location.fullName }
                    ])
                )
                : {};

            return {

                ...filters,
                city: filters.locations ? filters.locations : [],
                officeCity: filters.officeLocations ? filters.officeLocations : [],
                wantedKeywords: filters.wantedKeywords ? filters.wantedKeywords : [],
                unwantedKeywords: filters.unwantedKeywords ? filters.unwantedKeywords : [],
                filterId: filterId,
                name: filters.name ? filters.name : '',
                pushAlert: filters.alertPush ? filters.alertPush : false,
                alertEmail: filters.alertEmail ? filters.alertEmail : false,
                alertSms: filters.alertSms ? filters.alertSms : false,
                module: filters.module ? filters.module : '',
                notificationsDelay: filters.notificationsDelay !== null && filters.notificationsDelay !== undefined
                    ? filters.notificationsDelay.toString()
                    : '0',
                includeZeroArea: filters.includeZeroArea ? filters.includeZeroArea : false,
                offerAddedFrom: filters.offerAddedFrom ? filters.offerAddedFrom : '',
                offerAddedTo: filters.offerAddedTo ? filters.offerAddedTo : '',
                savedFilter: filters.savedFilter ? filters.savedFilter : false,
                locations: [],
                filterLocations: [],
                savedLocations: savedLocations,
                searchLocationResult: []
            };
        })
        .catch(() => {
            activeNotification('Problem z załadowaniem danych', 'Niestety nie udało się załadować danych. Spróbuj ponownie później', 'danger');
        });
};

const removeSavedFilter = (filterId: number) => {
    return axiosApiInstance
        .delete(`/v2/tender-offer/filter/delete?id=${filterId}`)
        .then(() => {
            activeNotification('Udało się!', 'Filtr został usunięty', 'success');
        })
        .catch(() => {
            activeNotification('Problem z usunięciem', 'Niestety nie udało się usunąć danych. Spróbuj ponownie później', 'danger');
        });
};

const updateFilter = async (params: TenderFilterReturnData) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, ...paramsWithoutLocations } = params;

    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'put',
        url: '/v2/tender-offer/filter/edit',
        data: {
            ...paramsWithoutLocations,
            // Backend zapisuje lokalizacje tylko z pola filterLocations (nowy standard OSM). Stare pole locations musimy pominąć,
            // inaczej backend tworzy duplikat wpisu w starym i nowym standardzie z różnymi ID.
            filterLocations: locationIds
        },
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został zaktualizowany', 'success');

            return resp;
        })
        .catch((err) => {
            activeNotification('Problem z zapisem', 'Niestety nie udało się zapisać danych. Spróbuj ponownie później', 'danger');

            return Promise.reject(err);
        });
};

const addFilter = (params: TenderFilterReturnData): Promise<AxiosResponse<number>> => {
    const savedLocations = Object.values(params.savedLocations).map((x) => {
        // @ts-expect-error dont need districts and cities
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { districts, cities, ...rest } = x;

        return { ...rest };
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, ...paramsWithoutLocations } = params;

    return axiosApiInstance({
        method: 'post',
        url: '/v2/tender-offer/filter/create',
        data: {
            ...paramsWithoutLocations,
            // Backend zapisuje lokalizacje tylko z pola filterLocations (nowy standard OSM). Stare pole locations musimy pominąć,
            // inaczej backend tworzy duplikat wpisu w starym i nowym standardzie z różnymi ID.
            filterLocations: savedLocations
        },
        paramsSerializer: function (params) {
            return qs.stringify(params);
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został dodany', 'success');

            return resp;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const getTenderOffers = (params: TenderFilterReturnData, page: number, size: number): Promise<TenderOffersResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, ...paramsWithoutLocations } = params;

    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'post',
        url: '/v2/tender-offer/advertisement',
        params: {
            page,
            size
        },
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds
        }
    })
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: TenderOfferResponseContent) => ({
                        ...el,
                        city: el.location,
                        dateAdded: el.dateAdded,
                        price: el.price ? convertPrice(el.price) : '',
                        pricePerSquareMeter: el.pricePerSquareMeter ? convertPrice(el.pricePerSquareMeter) : '',
                        area: el.area ? convertPrice(el.area) : '',
                        reaction: {
                            likeStatus: el.reaction?.likeStatus,
                            unlikeStatus: el.reaction?.unlikeStatus,
                            viewedStatus: el.reaction?.viewedStatus,
                            outdatedStatus: el.reaction?.outdatedStatus,
                            arrangedStatus: el.reaction?.arrangedStatus,
                            phoneStatus: el.reaction?.phoneStatus,
                            hideStatus: el.reaction?.hideStatus,
                            openedStatus: el.reaction?.openedStatus
                        }
                    }))
                };
            }

            return { empty: true, content: [], afterSearch: true };
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return { empty: true, content: [], afterSearch: true };
        });
};

const getTenderOfferDetails = async (id: number): Promise<TenderOfferDetailsResponse> => {
    return axiosApiInstance
        .get(`/v2/tender-offer/advertisement/details?id=${id}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getTenderOfferByHashId = async (hash: string): Promise<TenderDetailsResponse> => {
    return axiosApiInstance
        .get(`/v2/tender-offer/single-advertisement/detail?hash=${hash}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    price: resp.data.price ? convertPrice(resp.data.price) : ''
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return null;
        });
};

export const tenderOffersApiRequests = {
    getSavedFilters,
    getFilterDetailsTender,
    removeSavedFilter,
    updateFilter,
    addFilter,
    getTenderOffers,
    getTenderOfferDetails,
    getTenderOfferByHashId
};
